import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData, BinaryFiles } from "@excalidraw/excalidraw/types";

import { COLLAB_FILE_REQUEST_TIMEOUT_MS, WS_SUBTYPES } from "../app_constants";

import {
  validateCollabFile,
  type CollabFileMessageV1,
  type CollabFileValidationFailure,
} from "./roomProtocol";

export type CollabFileErrorReason = CollabFileValidationFailure | "sendFailed";

interface PendingFileRequest {
  requestedIds: FileId[];
  missingIds: Set<FileId>;
  loadedFiles: Map<FileId, BinaryFileData>;
  timeoutId: ReturnType<typeof setTimeout>;
  resolve: (result: CollabFileLoadResult) => void;
}

export interface CollabFileLoadResult {
  loadedFiles: BinaryFileData[];
  erroredFiles: Map<FileId, true>;
}

export interface CollabFileSaveResult {
  savedFiles: Map<FileId, BinaryFileData>;
  erroredFiles: Map<FileId, BinaryFileData>;
}

interface CollabFileRelayOptions {
  getCurrentClientId: () => string | null;
  getFiles: () => BinaryFiles;
  addFiles: (files: BinaryFileData[]) => void;
  sendMessage: (message: CollabFileMessageV1) => Promise<boolean>;
  onFileError: (reason: CollabFileErrorReason) => void;
  requestTimeoutMs?: number;
}

/** Coordinates encrypted file pushes and request responses for one room client. */
export class CollabFileRelay {
  private readonly pendingRequests = new Map<string, PendingFileRequest>();
  // Every peer holding a requested file answers the same `FILE_REQUEST`
  // (there is no central coordinator to pick one), so a request for many
  // ids in a room with several holders would otherwise multiply into one
  // response per holder per id. Since every relay frame is broadcast to the
  // whole room, each peer can also see responses that were not addressed to
  // it; this tracks `requestId:fileId` pairs already answered by anyone, so
  // a peer skips sending a response once it observes another one already
  // went out. Keyed by observation time so stale entries can be pruned.
  private readonly answeredFileRequests = new Map<string, number>();
  private readonly requestTimeoutMs: number;
  private requestSequence = 0;

  /** Stores the app callbacks needed to share files without owning UI state. */
  constructor(private readonly options: CollabFileRelayOptions) {
    this.requestTimeoutMs =
      options.requestTimeoutMs ?? COLLAB_FILE_REQUEST_TIMEOUT_MS;
  }

  /** Pushes each new file durably and records only sends the transport accepted. */
  saveFiles = async (
    addedFiles: Map<FileId, BinaryFileData>,
  ): Promise<CollabFileSaveResult> => {
    const savedFiles = new Map<FileId, BinaryFileData>();
    const erroredFiles = new Map<FileId, BinaryFileData>();
    for (const [fileId, candidate] of addedFiles) {
      const validation = validateCollabFile(candidate);
      if (!validation.valid || validation.file.id !== fileId) {
        erroredFiles.set(fileId, candidate);
        this.options.onFileError(
          validation.valid ? "malformed" : validation.reason,
        );
        continue;
      }
      try {
        const accepted = await this.options.sendMessage({
          type: WS_SUBTYPES.FILE_PUSH,
          payload: { file: validation.file },
        });
        if (accepted) {
          savedFiles.set(fileId, candidate);
        } else {
          erroredFiles.set(fileId, candidate);
          this.options.onFileError("sendFailed");
        }
      } catch (error) {
        console.error("Failed to push a collaboration file", error);
        erroredFiles.set(fileId, candidate);
        this.options.onFileError("sendFailed");
      }
    }
    return { savedFiles, erroredFiles };
  };

  /** Returns local files immediately and requests any remaining IDs from room peers. */
  getFiles = async (fileIds: FileId[]): Promise<CollabFileLoadResult> => {
    const requestedIds = [...new Set(fileIds)];
    const localFiles = this.options.getFiles();
    const loadedFiles = new Map<FileId, BinaryFileData>();
    const missingIds = new Set<FileId>();
    for (const fileId of requestedIds) {
      const localFile = localFiles[fileId];
      if (localFile) {
        const validation = validateCollabFile(localFile);
        if (validation.valid && validation.file.id === fileId) {
          loadedFiles.set(fileId, validation.file);
        } else {
          missingIds.add(fileId);
          this.options.onFileError(
            validation.valid ? "malformed" : validation.reason,
          );
        }
      } else {
        missingIds.add(fileId);
      }
    }
    if (missingIds.size === 0) {
      return {
        loadedFiles: [...loadedFiles.values()],
        erroredFiles: new Map(),
      };
    }

    const currentClientId = this.options.getCurrentClientId();
    if (!currentClientId) {
      return this.createLoadResult(requestedIds, loadedFiles, missingIds);
    }
    const requestId = `${currentClientId}_${this.requestSequence}`;
    this.requestSequence += 1;

    const resultPromise = new Promise<CollabFileLoadResult>((resolve) => {
      const timeoutId = setTimeout(() => {
        this.finishRequest(requestId);
      }, this.requestTimeoutMs);
      this.pendingRequests.set(requestId, {
        requestedIds,
        missingIds,
        loadedFiles,
        timeoutId,
        resolve,
      });
    });

    try {
      const accepted = await this.options.sendMessage({
        type: WS_SUBTYPES.FILE_REQUEST,
        payload: { requestId, fileIds: [...missingIds] },
      });
      if (!accepted) {
        this.options.onFileError("sendFailed");
        this.finishRequest(requestId);
      }
    } catch (error) {
      console.error("Failed to request collaboration files", error);
      this.options.onFileError("sendFailed");
      this.finishRequest(requestId);
    }
    return resultPromise;
  };

  /** Applies one validated peer file message and sends any required response. */
  handleMessage = async (
    senderClientId: string,
    message: CollabFileMessageV1,
  ): Promise<void> => {
    switch (message.type) {
      case WS_SUBTYPES.FILE_PUSH:
        this.receiveFile(message.payload.file);
        return;
      case WS_SUBTYPES.FILE_REQUEST:
        await this.respondToRequest(
          senderClientId,
          message.payload.requestId,
          message.payload.fileIds,
        );
        return;
      case WS_SUBTYPES.FILE_RESPONSE:
        // Recorded for every response, not only ones addressed to this
        // client: this is what lets `respondToRequest` notice a peer
        // already answered and skip sending a duplicate.
        this.markFileRequestAnswered(
          message.payload.requestId,
          message.payload.file.id,
        );
        if (
          message.payload.targetClientId === this.options.getCurrentClientId()
        ) {
          this.receiveResponse(message.payload.requestId, message.payload.file);
        }
    }
  };

  /** Resolves outstanding requests as errors when the collaboration session ends. */
  reset = (): void => {
    for (const requestId of [...this.pendingRequests.keys()]) {
      this.finishRequest(requestId);
    }
  };

  /** Sends one response message per valid file so each relay frame stays bounded. */
  private respondToRequest = async (
    targetClientId: string,
    requestId: string,
    fileIds: FileId[],
  ): Promise<void> => {
    const files = this.options.getFiles();
    for (const fileId of fileIds) {
      if (this.answeredFileRequests.has(`${requestId}:${fileId}`)) {
        // Another holder's response to this same request already went out
        // (observed via the room-wide broadcast, see `handleMessage`);
        // sending our own copy would only waste bandwidth and quota.
        continue;
      }
      const candidate = files[fileId];
      if (!candidate) {
        continue;
      }
      const validation = validateCollabFile(candidate);
      if (!validation.valid || validation.file.id !== fileId) {
        console.error(
          `Refusing invalid collaboration file response for ${fileId}`,
        );
        this.options.onFileError(
          validation.valid ? "malformed" : validation.reason,
        );
        continue;
      }
      // Marked before sending, not just on observing our own broadcast
      // response: the room relay never echoes a client's own frames back
      // to it, so without this a peer could never learn it already
      // answered and would repeat itself for a slow-draining request.
      this.markFileRequestAnswered(requestId, fileId);
      try {
        const accepted = await this.options.sendMessage({
          type: WS_SUBTYPES.FILE_RESPONSE,
          payload: {
            requestId,
            targetClientId,
            file: validation.file,
          },
        });
        if (!accepted) {
          console.error(
            `Collaboration file response was rejected for ${fileId}`,
          );
        }
      } catch (error) {
        console.error(
          `Failed to send collaboration file response for ${fileId}`,
          error,
        );
      }
    }
  };

  /** Records one request/file pair as answered, pruning entries older than two request timeouts. */
  private markFileRequestAnswered = (
    requestId: string,
    fileId: FileId,
  ): void => {
    const now = Date.now();
    this.answeredFileRequests.set(`${requestId}:${fileId}`, now);
    // A full sweep rather than a break-on-first-fresh-entry scan: re-marking
    // an existing key (this method runs both when observing and when about
    // to send) updates its timestamp without moving it, so an older
    // position can hold a newer timestamp and an early break could leave
    // genuinely stale entries behind.
    const staleBefore = now - this.requestTimeoutMs * 2;
    for (const [key, answeredAt] of this.answeredFileRequests) {
      if (answeredAt < staleBefore) {
        this.answeredFileRequests.delete(key);
      }
    }
  };

  /** Adds a pushed file and satisfies every request currently waiting for it. */
  private receiveFile = (file: BinaryFileData): void => {
    this.options.addFiles([file]);
    for (const [requestId, pending] of this.pendingRequests) {
      if (pending.missingIds.delete(file.id)) {
        pending.loadedFiles.set(file.id, file);
        if (pending.missingIds.size === 0) {
          this.finishRequest(requestId);
        }
      }
    }
  };

  /** Accepts a response only when its request explicitly asked for that file ID. */
  private receiveResponse = (requestId: string, file: BinaryFileData): void => {
    const pending = this.pendingRequests.get(requestId);
    if (!pending?.missingIds.has(file.id)) {
      return;
    }
    this.receiveFile(file);
  };

  /** Resolves one request with loaded files and true entries for every missing ID. */
  private finishRequest = (requestId: string): void => {
    const pending = this.pendingRequests.get(requestId);
    if (!pending) {
      return;
    }
    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(requestId);
    pending.resolve(
      this.createLoadResult(
        pending.requestedIds,
        pending.loadedFiles,
        pending.missingIds,
      ),
    );
  };

  /** Preserves requested order while building the FileManager load contract. */
  private createLoadResult = (
    requestedIds: FileId[],
    loadedFiles: Map<FileId, BinaryFileData>,
    missingIds: Set<FileId>,
  ): CollabFileLoadResult => ({
    loadedFiles: requestedIds.flatMap((fileId) => {
      const file = loadedFiles.get(fileId);
      return file ? [file] : [];
    }),
    erroredFiles: new Map(
      [...missingIds].map((fileId) => [fileId, true as const]),
    ),
  });
}
